import { Injectable, Pipe } from '@angular/core';

import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import {
    collection,
    collectionGroup,
    doc,
    docData,
    CollectionReference,
    Firestore,
    query,
    where,
    collectionData,
    setDoc,
    addDoc,
    orderBy,
    deleteDoc,
    QueryConstraint,
    QueryOrderByConstraint,
    getDocs,
} from '@angular/fire/firestore';

import { getCountFromServer, limit, startAfter, updateDoc, writeBatch } from 'firebase/firestore';
import { ICrudStrategy } from '../crud/icrud-strategy';
import { CrudQuery } from '../crud/crud.service';
import { User } from 'src/app/models/user';

type QWhere={
    field:string;
    operator:string;
    value:string;
}

type QOrder={
    direction:'ASC'|'DESC';
    field:string;
}

@Injectable({
    providedIn: 'root',
})
export class FirebaseLibService implements ICrudStrategy {
    strategy!: ICrudStrategy;
    table = '';

    constructor(
        private firestore: Firestore // eslint-disable-next-line no-empty-function
    ) {}

    setModel(table: string) {
        this.table = table;
    }

    findFirst<T>(id: string | number): Observable<T> {
        const docRef = doc(this.firestore, `${this.table}/${id}`);
        return docData(docRef, { idField: 'uid' as keyof T }) as Observable<T>;
    }

    findAll(): Observable<any[]> {
        const coll = collection(
            this.firestore,
            this.table
        ) as CollectionReference;

        return collectionData(query(coll), { idField: 'uid' });
    }


    async countCollection(): Promise<number> {
        const collRef = collection(this.firestore, this.table);
        const snapshot = await getCountFromServer(collRef);
        return snapshot.data().count;
    }


    findAllCollectionGroup(): Observable<any[]> {
        const coll = collectionGroup(
            this.firestore,
            this.table
        ) as CollectionReference;

        return collectionData(query(coll), { idField: 'uid' });
    }

    search(queryF: CrudQuery): any {
        return collectionData(
            query(
                collection(this.firestore, this.table) as CollectionReference,
                where(queryF.field, queryF.operator, queryF.value),
                orderBy(queryF.orderBy, queryF.orderByDirection)
            ),
            { idField: 'uid' }
        );
    }

    searchCollectionGroup(queryF: CrudQuery): any {
        return collectionData(
            query(
                collectionGroup(
                    this.firestore,
                    this.table
                ) as CollectionReference,
                where(queryF.field, queryF.operator, queryF.value),
                orderBy(queryF.orderBy, queryF.orderByDirection)
            ),
            { idField: 'uid' }
        );
    }

    
    searchCollectionGroupMany(queryF: any, order: any,realTime:boolean = false): any {

         const whereF: QueryConstraint[] = [];
        const orderF: QueryOrderByConstraint[] = [];
        queryF.forEach((item: any) => {
            whereF.push(where(item.field, item.operator, item.value));
        });

        order.forEach((item: any) => {
            orderF.push(orderBy(item.orderBy, item.orderByDirection));
        });

        const q = query(
            collectionGroup(this.firestore, this.table) as CollectionReference,
            ...whereF,
            ...orderF
        );

        if (!realTime) {
            return from(getDocs(q)).pipe(
                map(snapshot => snapshot.docs.map(d => ({ ...d.data(), uid: d.id })))
            );
        }

        return collectionData(q, { idField: 'uid' });
    }

    create<T>(id: string | number, obj: T): Promise<void> {
        const userDoc = doc(this.firestore, `${this.table}/${id}`);
        return setDoc(userDoc, obj as any);
    }

   /*  async add<T>(obj: T): Promise<string | number> {
        const docRef = collection(this.firestore, this.table);
        const result = await addDoc(docRef, obj as any);
        return result.id;
    }
 */
    //este metodo ademas de crear el objeto agrega el campo "id"
    async add<T>(obj: T): Promise<string | number> {
        const collRef = collection(this.firestore, this.table);
        const docRef = doc(collRef);
        const withUid = { ...(obj as any), id: docRef.id };
        await setDoc(docRef, withUid as any);
        return docRef.id;
    }

    async addMany<T>(objectsArray: T[]): Promise<void> {
        const colRef = collection(this.firestore, this.table);
        const batch = writeBatch(this.firestore);
        objectsArray.forEach(obj => {
            const newDoc = doc(colRef);
            batch.set(newDoc, obj as any);
        });
        await batch.commit();
    }

    /* Actualizar una colección completa, por ejemplo una sublista. Compara con la lista original para decidir
    si debe borrar, agregar o actualizar cada objeto */
     async updateMany(oldObjects: any[],newObjects: any[]): Promise<void> {
        const subColRef = collection(this.firestore, this.table);
        const batch = writeBatch(this.firestore);

        // Mapear por id para comparar
        const oldMap = new Map(oldObjects.filter(a => a.uid).map(a => [a.uid, a]));
        const newMap = new Map(newObjects.filter(a => a.uid).map(a => [a.uid, a]));
        
        // Borrar los que estaban antes y ya no están
        oldMap.forEach((object, key) => {
            if (!newMap.has(key)){
                const docRef = doc(subColRef, object.uid ?? key);
                batch.delete(docRef);
            }
        });
        
        // Actualizar los que ya tienen uid
        newMap.forEach((object, key) => {
            const docRef = doc(subColRef, object.uid);
            batch.set(docRef, object, { merge: true });
        });

        // Agregar los nuevos
        newObjects.filter(a => !a.uid).forEach(object => {
            const docRef = doc(subColRef);
            batch.set(docRef, object);
        });
        
        // Ejecutar todo en una sola llamada
        await batch.commit();
    }

    // Borra un conjunto de documentos, no borra sus subcolecciones
    async deleteMany(deleteObjects: any[]): Promise<void> {
        const subColRef = collection(this.firestore, this.table);
        const batch = writeBatch(this.firestore);
        
        // Borrar los que estaban antes y ya no están
        deleteObjects.forEach((object, key) => {
            const docRef = doc(subColRef, object.uid ?? key);
            batch.delete(docRef);
        });

        // Ejecutar todo en una sola llamada
        await batch.commit();
    }

/* actualiza una lista de varios objetos. Pensado para subcolecciones.
Fuerza actualizar, no crea ni elimina.  */
    async updateOnlyMany(objects: any[]): Promise<void> {
        const subColRef = collection(this.firestore, this.table);
        const batch = writeBatch(this.firestore);

        // Mapear por id para comparar
        const idMap = new Map(objects.filter(a => a.uid).map(a => [a.uid, a]));
        
        // Actualizar los que ya tienen uid
        idMap.forEach((object, key) => {
            const docRef = doc(subColRef, object.uid);
            batch.set(docRef, object, { merge: true });
        });
        
        // Ejecutar todo en una sola llamada
        await batch.commit();
    }

    /* id nuevo para un documento de esa colección, sin crearlo (para usarlo en commitWrites) */
    newDocId(collectionPath: string): string {
        return doc(collection(this.firestore, collectionPath)).id;
    }

    /* Escribe varios documentos de distintas colecciones de una sola vez: si falla una escritura, no se hace ninguna.
    set crea o reemplaza (con merge: true solo cambia los campos que vienen); update cambia campos de un documento existente */
    async commitWrites(writes: { path: string, data: any, mode: 'set' | 'update', merge?: boolean }[]): Promise<void> {
        const batch = writeBatch(this.firestore);
        writes.forEach(w => {
            const docRef = doc(this.firestore, w.path);
            if (w.mode === 'update') {
                batch.update(docRef, w.data);
            } else if (w.merge) {
                batch.set(docRef, w.data, { merge: true });
            } else {
                batch.set(docRef, w.data);
            }
        });
        await batch.commit();
    }

    update<T>(id: string | number, obj: T): Promise<void> {
        const userDoc = doc(this.firestore, `${this.table}/${id}`);
        return updateDoc(userDoc, obj as any);
    }

    async delete<T>(id: string | number): Promise<void> {
        const userDoc = doc(this.firestore, `${this.table}/${id}`);
        return await deleteDoc(userDoc);
    }

    simpleSearch<T>(field: string, value: string): Observable<T[]> {
        const coll = collection(
            this.firestore,
            this.table
        ) as CollectionReference;
        return collectionData(query(coll, where(field, '==', value)), {
            idField: 'uid',
        }) as Observable<T[]>;
    }

    findWhere(queryF: any, order: any): Observable<any[]> {
        const whereF: QueryConstraint[] = [];
        const orderF: QueryOrderByConstraint[] = [];
        queryF.forEach((item: any) => {
            whereF.push(where(item.field, item.operator, item.value));
        });

        order.forEach((item: any) => {
            orderF.push(orderBy(item.orderBy, item.orderByDirection));
        });

        return collectionData(
            query(
                collection(this.firestore, this.table) as CollectionReference,
                ...whereF,
                ...orderF
            ),
            { idField: 'uid' }
        );
    }

    findOneWhere<T>(queryF: any, order: any): Observable<T | null> {
        const whereF: QueryConstraint[] = [];
        const orderF: QueryOrderByConstraint[] = [];

        queryF.forEach((item: any) => {
            whereF.push(where(item.field, item.operator, item.value));
        });

        order.forEach((item: any) => {
            orderF.push(orderBy(item.orderBy, item.orderByDirection));
        });

        const q = query(
            collection(this.firestore, this.table) as CollectionReference,
            ...whereF,
            ...orderF,
            limit(1)
        );

        return collectionData(q, { idField: 'uid' }).pipe(
            map(results => results.length ? results[0] as T : null)
        );
    }
    

    findWhereCount(queryF: any, order: any): Promise<number> {
        const whereF: QueryConstraint[] = [];
        const orderF: QueryOrderByConstraint[] = [];

        queryF.forEach((item: any) => {
            whereF.push(where(item.field, item.operator, item.value));
        });

        order.forEach((item: any) => {
            orderF.push(orderBy(item.orderBy, item.orderByDirection));
        });

        const q = query(
            collection(this.firestore, this.table),
            ...whereF,
            ...orderF
        );

        return getCountFromServer(q).then((snapshot) => snapshot.data().count);
    }
    findWhereGroupCount(queryF: any, order: any): Promise<number> {
        const whereF: QueryConstraint[] = [];
        const orderF: QueryOrderByConstraint[] = [];

        queryF.forEach((item: any) => {
            whereF.push(where(item.field, item.operator, item.value));
        });

        order.forEach((item: any) => {
            orderF.push(orderBy(item.orderBy, item.orderByDirection));
        });

        const q = query(
            collectionGroup(this.firestore, this.table),
            ...whereF,
            ...orderF
        );

        return getCountFromServer(q).then((snapshot) => snapshot.data().count);
    }


    
    // TO DO: Hacer mas general
    getUserAccount(userId: string): Observable<User | null> {
        const accountsRef = collection(this.firestore, `users/${userId}/accounts`);
    
        return from(getDocs(accountsRef)).pipe(
            switchMap(snapshot => {
                if (snapshot.empty) {
                    console.warn(`No accounts found for user ${userId}`);
                    return of(null);
                }
                const firstAccount = snapshot.docs[0]; // Get first account type (e.g., "customer")
    
                // Fetch the actual account data
                const accountDocRef = doc(this.firestore, `users/${userId}/accounts/${firstAccount.id}`);
                return docData(accountDocRef, { idField: 'uid' as keyof User }) as Observable<User>;
            }),
            catchError(error => {
                console.error(`Error fetching accounts for user ${userId}:`, error);
                return of(null);
            })
        );
    }

    getUserByQuery(options:{where: QWhere[], order: any[],type?:string}): Observable<any[]> {
        const whereF: QueryConstraint[] = [];
        const orderF: QueryOrderByConstraint[] = [];
        options.where.forEach((item: any) => {
            whereF.push(where(item.field, item.operator, item.value));
        });

        options.order.forEach((item: any) => {
            orderF.push(orderBy(item.orderBy, item.orderByDirection));
        });

        return collectionData(
            query(
                collectionGroup(this.firestore, 'accounts') as CollectionReference,
                ...whereF,
                ...orderF
            ),
            { idField: 'uid' }
        );
    }
    
}
